import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import ThemeToggle from './ThemeToggle';

const DarkModeTest: React.FC = () => {
  const { theme, actualTheme, setTheme } = useTheme();

  return (
    <div className="p-6 bg-white dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Dark Mode Test
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Theme Status */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Current Theme Status
            </h2>
            <div className="space-y-2">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Selected Theme:</strong> {theme}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Actual Theme:</strong> {actualTheme}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>System Preference:</strong> {window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'}
              </p>
            </div>
          </div>

          {/* Theme Controls */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Theme Controls
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Theme Selector
                </label>
                <ThemeToggle variant="select" showLabel={false} />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Toggle Button
                </label>
                <ThemeToggle variant="button" showLabel={false} />
              </div>
            </div>
          </div>

          {/* Color Samples */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Color Samples
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-500 text-white p-4 rounded text-center">
                Blue 500
              </div>
              <div className="bg-green-500 text-white p-4 rounded text-center">
                Green 500
              </div>
              <div className="bg-red-500 text-white p-4 rounded text-center">
                Red 500
              </div>
              <div className="bg-yellow-500 text-black p-4 rounded text-center">
                Yellow 500
              </div>
            </div>
          </div>

          {/* Text Samples */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Text Samples
            </h2>
            <div className="space-y-2">
              <p className="text-gray-900 dark:text-white font-bold">
                Bold text (primary)
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Regular text (secondary)
              </p>
              <p className="text-gray-500 dark:text-gray-400">
                Muted text (tertiary)
              </p>
              <p className="text-blue-600 dark:text-blue-400">
                Link text (accent)
              </p>
            </div>
          </div>

          {/* Component Samples */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Component Samples
            </h2>
            <div className="space-y-4">
              <button className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors">
                Primary Button
              </button>
              <button className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                Secondary Button
              </button>
              <input 
                type="text" 
                placeholder="Input field"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Border Samples */}
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Border Samples
            </h2>
            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 p-4 rounded">
                Light border
              </div>
              <div className="border-2 border-blue-500 p-4 rounded">
                Accent border
              </div>
              <div className="border border-red-300 dark:border-red-600 p-4 rounded">
                Error border
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DarkModeTest;
